// web/src/routes/supplements/SupplementOrders.jsx
// Everything that needs ordering, grouped by supplement.
//
// Taylor's point 5, nearly verbatim: "I want to be able to see everything that
// needs ordering grouped by supplement rather than placing individual orders
// by horse or by owner. For example, I could open Isoxsuprine and see which
// owners need it, how many containers each needs, and the total quantity to
// order."
//
// So the grouping is the page. Vendor first, because that is the phone call —
// eight of Westwood's nine products come from the same vet's office, and a
// list sorted by product name would have her ringing them four times.
//
// THREE DIFFERENT REASONS A ROW IS HERE
//
//   running low   the sums say so. Nobody has decided anything yet, and the
//                 row carries a suggested quantity rather than a real one.
//   on the list   somebody marked it. This is what the totals count.
//   ordered       placed and not yet arrived, kept visible so it is not
//                 ordered twice.
//
// Autoships appear and are never counted: Taylor wants to know Rose's
// Farrier's Formula is due without being told to go and buy it.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtDateNear } from '../../lib/dates';
import { Panel, Empty, PlannerSkeleton } from '../../components/dashboard/Planner';

export default function SupplementOrders() {
  const { data, isLoading } = useQuery({
    queryKey: ['supplement-orders'],
    queryFn: () => api.get('/supplements/orders'),
  });

  const groups = data?.groups ?? [];
  const byVendor = groupByVendor(groups);
  const toOrder = groups.reduce((n, g) => n + g.totalToOrder, 0);
  const flagged = groups.reduce((n, g) => n + g.flaggedCount, 0);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="flex items-end justify-between gap-3 mb-4 pb-2 border-b-2 border-hunter/30">
        <div className="min-w-0">
          <Link to="/supplements" className="text-xs text-hunter hover:underline">← Supplements</Link>
          <h1 className="text-lg md:text-xl font-semibold leading-tight">Ordering</h1>
          <p className="text-sm text-ink-500">
            {toOrder > 0 ? `${toOrder} to order` : 'Nothing marked to order'}
            {flagged > 0 && <span className="text-amber-700"> · {flagged} running low</span>}
          </p>
        </div>
      </header>

      {isLoading ? (
        <PlannerSkeleton />
      ) : groups.length === 0 ? (
        <Panel title="Ordering">
          <Empty>
            Nothing needs ordering, and nothing is on the way.{' '}
            <Link to="/supplements" className="text-hunter hover:underline">All supplements →</Link>
          </Empty>
        </Panel>
      ) : (
        <div className="space-y-5">
          {byVendor.map(({ vendor, items }) => (
            <section key={vendor ?? 'none'}>
              {/* One heading per phone call. */}
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                {vendor ?? 'No vendor set'}
              </h2>
              <div className="space-y-3">
                {items.map((g) => (
                  <Group key={g.supplementId} group={g} today={data.today} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function Group({ group: g, today }) {
  return (
    <Panel
      title={g.name}
      action={
        <Link to={`/supplements/${g.supplementId}?tab=ordering`} className="normal-case tracking-normal hover:underline">
          mark these →
        </Link>
      }
    >
      <ul className="planner-rows">
        {g.lines.map((l) => (
          <li key={l.ownerUserId} className="px-3 py-2 text-sm flex items-baseline gap-3">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{l.name}</span>
              <Reason line={l} today={today} />
            </span>
            <span className="shrink-0 text-right">
              <span className="block tabular-nums leading-none">
                {/* The real quantity where a person set one; the suggestion in
                    lighter type where they haven't, so the two never look the
                    same at a glance. */}
                {l.status === 'none' ? (
                  <span className="text-ink-400">{l.suggestedQuantity ?? '—'}</span>
                ) : (
                  <strong>{l.quantity}</strong>
                )}
              </span>
              <span className="block text-[11px] text-ink-400">{l.containers} on hand</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="px-3 py-2 border-t border-hunter/20 flex items-baseline justify-between text-sm">
        <span className="text-ink-500">
          {g.containerLabel ? `${g.containerLabel} · ` : ''}
          {g.leadTimeDays} day lead
        </span>
        <span className="font-medium tabular-nums">
          {g.totalToOrder > 0 ? `${g.totalToOrder} to order` : 'nothing marked'}
        </span>
      </div>
    </Panel>
  );
}

function Reason({ line: l, today }) {
  if (l.status === 'ordered') {
    return (
      <span className="block text-xs text-hunter">
        Ordered{l.placedOn ? ` ${fmtDateNear(l.placedOn, today)}` : ''} — on the way
      </span>
    );
  }
  if (l.status === 'needed') {
    return <span className="block text-xs text-amber-700 font-medium">On the list</span>;
  }
  if (l.onAutoship) {
    return (
      <span className="block text-xs text-sky-700">
        Autoship{l.autoship?.nextExpected ? ` · next ${fmtDateNear(l.autoship.nextExpected, today)}` : ''} — not yours to order
      </span>
    );
  }
  return (
    <span className="block text-xs text-amber-700">
      {l.containers <= 0 ? 'Out' : 'Running low'}
      {l.emptyOn ? ` · empty ${fmtDateNear(l.emptyOn, today)}` : ''}
    </span>
  );
}

/** Groups in the order the server sent them, bucketed by vendor. */
function groupByVendor(groups) {
  const out = [];
  for (const g of groups) {
    const last = out[out.length - 1];
    if (last && last.vendor === g.vendorName) last.items.push(g);
    else out.push({ vendor: g.vendorName, items: [g] });
  }
  return out;
}
